export type ComplianceDataState = 'UNKNOWN' | 'KNOWN_BAD' | 'NON_NEGOTIABLE';

export type ComplianceRegulatoryClass =
  | 'SCREENING'
  | 'TRAINING'
  | 'AHPRA_REGISTRATION'
  | 'WORK_RIGHTS'
  | 'ROLE_CERTIFICATION'
  | 'ASSIGNMENT_REQUIREMENT'
  | 'ENFORCED_POLICY'
  | 'LEAVE_ABSENCE'
  | 'NON_SAFETY_ADVISORY'
  | 'UNKNOWN_RULE';

export type ComplianceAttestationArtefact =
  | 'STATUTORY_DECLARATION'
  | 'SUPERVISION_ARRANGEMENT'
  | 'PROOF_OF_LODGED_APPLICATION'
  | 'AUDITED_ATTESTATION'
  | 'TIME_BOXED_FOLLOW_UP_TASK'
  | 'ROLE_CERTIFICATION_REGULATORY_CLASS';

export interface ComplianceRuleClassification {
  state: ComplianceDataState;
  provisional_eligible: boolean;
  regulatory_class: ComplianceRegulatoryClass;
  attestation_artefacts_required: ComplianceAttestationArtefact[];
}

type ImmutableRuleClassification = Omit<
  ComplianceRuleClassification,
  'attestation_artefacts_required'
> & {
  readonly attestation_artefacts_required: readonly ComplianceAttestationArtefact[];
};

export const COMPLIANCE_RULE_CODES = [
  'MISSING_SCREENING',
  'EXPIRED_SCREENING',
  'MISSING_REQUIRED_TRAINING',
  'EXPIRED_REQUIRED_TRAINING',
  'AHPRA_REGISTRATION_MISSING',
  'AHPRA_REGISTRATION_EXPIRED',
  'WORK_RIGHTS_EXPIRED',
  'REQUIRED_ROLE_CERTIFICATION_MISSING',
  'ROLE_CERTIFICATION_INVALID_OR_EXPIRED',
  'ASSIGNMENT_REQUIREMENT_BLOCKED',
  'ASSIGNMENT_REQUIREMENT_WARNING',
  'ENFORCED_POLICY_ACKNOWLEDGEMENT_REQUIRED',
  'MISSING_CLASSIFICATION',
  'INSUFFICIENT_CLASSIFICATION',
  'RATE_VERSION_MISSING',
  'ABSENCE_PLANE_UNAVAILABLE',
] as const;

export type ComplianceRuleCode = (typeof COMPLIANCE_RULE_CODES)[number];

const RULE_CLASSIFICATIONS = {
  MISSING_SCREENING: {
    state: 'UNKNOWN',
    provisional_eligible: true,
    regulatory_class: 'SCREENING',
    attestation_artefacts_required: [
      'STATUTORY_DECLARATION',
      'SUPERVISION_ARRANGEMENT',
      'PROOF_OF_LODGED_APPLICATION',
    ],
  },
  EXPIRED_SCREENING: {
    state: 'KNOWN_BAD',
    provisional_eligible: false,
    regulatory_class: 'SCREENING',
    attestation_artefacts_required: [],
  },
  MISSING_REQUIRED_TRAINING: {
    // POLICY: a training code or acknowledgement alone carries no cited legal floor.
    // Tenant opt-in is enforced at first assignment, never by refusing care delivery.
    state: 'UNKNOWN',
    provisional_eligible: false,
    regulatory_class: 'NON_SAFETY_ADVISORY',
    attestation_artefacts_required: [],
  },
  EXPIRED_REQUIRED_TRAINING: {
    // POLICY: a training code or acknowledgement alone carries no cited legal floor.
    // Tenant opt-in is enforced at first assignment, never by refusing care delivery.
    state: 'KNOWN_BAD',
    provisional_eligible: false,
    regulatory_class: 'NON_SAFETY_ADVISORY',
    attestation_artefacts_required: [],
  },
  AHPRA_REGISTRATION_MISSING: {
    // EMPTY-EVIDENCE CLASS. "Missing" means Statura holds no registration record. It
    // does NOT mean the practitioner is unregistered — AHPRA registration is a fact
    // about the practitioner, not about our database, and it is independently
    // checkable on the public national register.
    //
    // Classified NON_NEGOTIABLE with no exit, this asserted "this nurse is not
    // registered" on the strength of a row we never captured. Every live tenant
    // carries `staff.ahpra_verification_status = 'not_verified'` for 100% of workers
    // because that is the COLUMN DEFAULT, so the assertion would have been made
    // against every nurse on the platform the moment a tenant left WARN mode.
    //
    // The publish trigger already reached the right answer independently: this code
    // sits in the WARN-mode exempt list in `tg_roster_period_publish_hard_gate`
    // alongside the other four absence rules. TS was the stricter, wrong half.
    //
    // KNOWN-BAD IS UNCHANGED: AHPRA_REGISTRATION_EXPIRED stays NON_NEGOTIABLE with no
    // exit. A lapsed registration is a recorded adverse fact and must keep blocking.
    //
    // The exit is deliberately the strictest in this registry — a statutory
    // declaration alone is not enough for a health-practitioner registration. It
    // requires proof of a lodged application AND a supervision arrangement, both
    // time-boxed. This is a runway to capture evidence, not a substitute for it.
    //
    // PROPER FIX, NOT YET BUILT: verify against the AHPRA public register rather than
    // attest. This rule is the one member of the empty-evidence class where the
    // authoritative source is public and machine-checkable, so an attestation runway
    // is the interim answer, not the destination.
    state: 'UNKNOWN',
    provisional_eligible: true,
    regulatory_class: 'AHPRA_REGISTRATION',
    attestation_artefacts_required: [
      'STATUTORY_DECLARATION',
      'PROOF_OF_LODGED_APPLICATION',
      'SUPERVISION_ARRANGEMENT',
    ],
  },
  AHPRA_REGISTRATION_EXPIRED: {
    state: 'NON_NEGOTIABLE',
    provisional_eligible: false,
    regulatory_class: 'AHPRA_REGISTRATION',
    attestation_artefacts_required: [],
  },
  WORK_RIGHTS_EXPIRED: {
    state: 'NON_NEGOTIABLE',
    provisional_eligible: false,
    regulatory_class: 'WORK_RIGHTS',
    attestation_artefacts_required: [],
  },
  REQUIRED_ROLE_CERTIFICATION_MISSING: {
    // "Missing" means Statura holds no certification record — it does NOT mean the
    // worker is uncertified. An absent record was treated as a known-bad fact with
    // no exit for anyone.
    //
    // CORRECTION (2026-08-06): the original note here said this rule "alone" was
    // NON_NEGOTIABLE. That was wrong — AHPRA_REGISTRATION_MISSING and
    // ENFORCED_POLICY_ACKNOWLEDGEMENT_REQUIRED had the identical shape, and
    // MISSING_CLASSIFICATION and RATE_VERSION_MISSING were KNOWN_BAD on an absence.
    // Fixing this rule in isolation left four siblings live. They are now all
    // reclassified and held together by EMPTY_EVIDENCE_RULE_CODES below.
    //
    // It was also the only rule in this registry carrying a non-empty
    // attestation_artefacts_required list while provisional_eligible was false, i.e.
    // an attestation path that was declared and unreachable.
    //
    // KNOWN-BAD stays blocked: ROLE_CERTIFICATION_INVALID_OR_EXPIRED is unchanged.
    // The exit is a statutory declaration plus a mandatory 1-90 day expiry, so it is a
    // recorded, attributed, time-boxed runway to capture the evidence — not a bypass.
    // The full screening artefact set is required, not a bare declaration. This rule
    // bundles provider training (CPR, FIRST_AID, MANUAL_HANDLING, DEMENTIA_CARE,
    // INFECTION_PREVENTION_CONTROL) with statutory screening (NATIONAL_POLICE_CHECK,
    // WORKER_SCREENING_CLEARANCE) in a single gate, and a provider with no screening
    // records has no MISSING_SCREENING gate to carry the statutory obligation
    // separately. Accepting a lone statutory declaration here would therefore clear an
    // aged-care screening obligation on weaker evidence than Aged Care Rules 2025
    // r152.20/152.25 require of MISSING_SCREENING. Hold this gate to the same bar.
    state: 'UNKNOWN',
    provisional_eligible: true,
    regulatory_class: 'ROLE_CERTIFICATION',
    attestation_artefacts_required: [
      'STATUTORY_DECLARATION',
      'SUPERVISION_ARRANGEMENT',
      'PROOF_OF_LODGED_APPLICATION',
    ],
  },
  ROLE_CERTIFICATION_INVALID_OR_EXPIRED: {
    state: 'KNOWN_BAD',
    provisional_eligible: false,
    regulatory_class: 'ROLE_CERTIFICATION',
    attestation_artefacts_required: [],
  },
  ASSIGNMENT_REQUIREMENT_BLOCKED: {
    state: 'NON_NEGOTIABLE',
    provisional_eligible: false,
    regulatory_class: 'ASSIGNMENT_REQUIREMENT',
    attestation_artefacts_required: [],
  },
  ASSIGNMENT_REQUIREMENT_WARNING: {
    state: 'UNKNOWN',
    provisional_eligible: false,
    regulatory_class: 'NON_SAFETY_ADVISORY',
    attestation_artefacts_required: [],
  },
  ENFORCED_POLICY_ACKNOWLEDGEMENT_REQUIRED: {
    // POLICY: a training code or acknowledgement alone carries no cited legal floor.
    // Tenant opt-in is enforced at first assignment, never by refusing care delivery.
    state: 'UNKNOWN',
    provisional_eligible: false,
    regulatory_class: 'NON_SAFETY_ADVISORY',
    attestation_artefacts_required: [],
  },
  MISSING_CLASSIFICATION: {
    // EMPTY-EVIDENCE CLASS. No classification recorded against the worker. KNOWN_BAD
    // asserted the pay classification was wrong when in fact none was captured.
    //
    // This mislabel had teeth: the softening guard correctly refuses to relax any
    // KNOWN_BAD rule, so classifying an absence as KNOWN_BAD made it permanently
    // unsoftenable — a provider who had loaded no classification data could not be
    // relieved of a rule about data they had not yet entered. The guard was right;
    // the classification it was reading was wrong.
    //
    // INSUFFICIENT_CLASSIFICATION stays KNOWN_BAD deliberately: there the worker HAS
    // a recorded classification and it sits below the level the work requires. That
    // is a present record carrying an adverse fact, which is exactly what KNOWN_BAD
    // is for.
    state: 'UNKNOWN',
    provisional_eligible: false,
    regulatory_class: 'NON_SAFETY_ADVISORY',
    attestation_artefacts_required: [],
  },
  INSUFFICIENT_CLASSIFICATION: {
    state: 'KNOWN_BAD',
    provisional_eligible: false,
    regulatory_class: 'NON_SAFETY_ADVISORY',
    attestation_artefacts_required: [],
  },
  RATE_VERSION_MISSING: {
    // EMPTY-EVIDENCE CLASS. No rate version is configured for the pay context. That
    // is a gap in OUR configuration, not an adverse fact about the worker or the
    // shift, and KNOWN_BAD made it unsoftenable for exactly the providers who have
    // not finished configuring pay.
    state: 'UNKNOWN',
    provisional_eligible: false,
    regulatory_class: 'NON_SAFETY_ADVISORY',
    attestation_artefacts_required: [],
  },
  ABSENCE_PLANE_UNAVAILABLE: {
    state: 'NON_NEGOTIABLE',
    provisional_eligible: false,
    regulatory_class: 'LEAVE_ABSENCE',
    attestation_artefacts_required: [],
  },
} as const satisfies Record<ComplianceRuleCode, ImmutableRuleClassification>;

/**
 * Rules that fire because Statura holds NO record, as opposed to holding a record
 * that carries an adverse fact.
 *
 * This list exists so the invariant below can be enforced mechanically. Four rules
 * in this registry were classified KNOWN_BAD or NON_NEGOTIABLE on an absence, and
 * each was found one at a time, months apart, only after it caused a live incident.
 * The point of naming the class is that the fifth one fails a test instead.
 *
 * Membership is decided by what makes the rule FIRE, not by the spelling of its
 * code: INSUFFICIENT_CLASSIFICATION is not a member (a classification is recorded
 * and is below the required level), while RATE_VERSION_MISSING is.
 */
export const EMPTY_EVIDENCE_RULE_CODES = [
  'MISSING_SCREENING',
  'MISSING_REQUIRED_TRAINING',
  'AHPRA_REGISTRATION_MISSING',
  'REQUIRED_ROLE_CERTIFICATION_MISSING',
  'ENFORCED_POLICY_ACKNOWLEDGEMENT_REQUIRED',
  'MISSING_CLASSIFICATION',
  'RATE_VERSION_MISSING',
] as const satisfies readonly ComplianceRuleCode[];

export type EmptyEvidenceRuleCode = (typeof EMPTY_EVIDENCE_RULE_CODES)[number];

export function isEmptyEvidenceRule(ruleCode: string | null | undefined): boolean {
  return (EMPTY_EVIDENCE_RULE_CODES as readonly string[]).includes(
    normaliseComplianceRuleCode(ruleCode),
  );
}

const UNKNOWN_RULE_CLASSIFICATION: ImmutableRuleClassification = {
  state: 'UNKNOWN',
  provisional_eligible: false,
  regulatory_class: 'UNKNOWN_RULE',
  attestation_artefacts_required: [],
};

export function normaliseComplianceRuleCode(ruleCode: string | null | undefined): string {
  return String(ruleCode ?? '')
    .trim()
    .toUpperCase();
}

function isComplianceRuleCode(ruleCode: string): ruleCode is ComplianceRuleCode {
  return (COMPLIANCE_RULE_CODES as readonly string[]).includes(ruleCode);
}

function cloneClassification(policy: ImmutableRuleClassification): ComplianceRuleClassification {
  return {
    ...policy,
    attestation_artefacts_required: [...policy.attestation_artefacts_required],
  };
}

export function classifyComplianceRule(
  ruleCode: string | null | undefined,
): ComplianceRuleClassification {
  const normalised = normaliseComplianceRuleCode(ruleCode);
  if (!isComplianceRuleCode(normalised)) {
    return cloneClassification(UNKNOWN_RULE_CLASSIFICATION);
  }
  return cloneClassification(RULE_CLASSIFICATIONS[normalised]);
}
